import type { Authorship } from '@palaccount/contracts';
import { useTranslation } from 'react-i18next';

import { Ltr } from '@/components/amount';

/**
 * The showing half of MD-RULE-5, once, for every master-data screen.
 *
 * PART 5, spec line 4744, verbatim:
 *
 *   MD-RULE-5  Every master record must show who created it and when, and who rule-coverage:ignore
 *              last changed it and when.
 *
 * ── FOUR FACTS, NOT TWO ───────────────────────────────────────────────────
 *
 * The sentence names four things: a creator, a creation time, a last changer
 * and a change time. It is one clause repeated, not one clause plus a
 * qualifier, so a screen showing only "created by" has met half of it.
 *
 * The spec's own field tables are NARROWER THAN THE RULE and must not be read
 * as the specification of it. Exactly four of the fourteen MD screens carry a
 * `Created by / on` row (MD-04 line 5960, MD-07 6425, MD-09 6924, MD-14 7841)
 * and NOT ONE carries a "changed by" row anywhere in the document. The rule
 * governs the whole part — it sits under the heading "The rule that governs
 * this entire part" — so four facts on every master record is the requirement
 * and the tables merely illustrate one of them on a few screens.
 *
 * What the tables DO supply is the format, and it is followed here verbatim:
 *
 *   | 10 | Created by / on | Date and time (read-only) | — | `Sarah Chen · 2026-01-05 14:00` |
 *
 * A name, a middle dot, then `YYYY-MM-DD HH:MM`. The date rendering is
 * `slice(0, 16).replace('T', ' ')` inside an `<Ltr>` — the in-house form
 * already used for the CRM activity author lines, which are the same
 * `createdBy` + `createdByName` pair this shape follows.
 */

/**
 * ── THE SEAM (OWNER DECISION 28) ──────────────────────────────────────────
 *
 * THIS FUNCTION RENDERS NOTHING TODAY, DELIBERATELY. It is not an
 * unimplemented branch and it is not a bug — it is a refusal to assert
 * something the server cannot currently tell us.
 *
 * A master row arrives with a NULL author for TWO DIFFERENT REASONS, and
 * NOTHING ON THE WIRE SEPARATES THEM:
 *
 *   1. THE SYSTEM DID IT. `checks.service.ts:885` sets
 *      `{ bouncedCheque: true, updatedAt: new Date(), updatedBy: null }` on a
 *      partner when a cheque bounces (BR-FIN-10-17). `partners` is one of the rule-coverage:ignore
 *      nine tables carrying these columns, so this is a live producer, not a
 *      theoretical one. `number_sequences` is the same story on the create
 *      side: those rows are minted by `app_ensure_number_sequences()` at
 *      signup, so `created_by` there is permanently NULL.
 *   2. NOBODY RECORDED IT. The row predates migration 20260901100000, whose
 *      columns were added nullable with no default precisely so that a
 *      backfill would not name a person as the author of a record they never
 *      touched.
 *
 * Both arrive as `createdBy`/`createdByName` = `null`. So "System" would lie
 * about every pre-migration row and "Not recorded" would lie about every
 * bounced cheque, and there is no third word that is true of both. The spec
 * does not rule on it — it never says what to draw when there is nobody — so
 * choosing here would be inventing a requirement rather than meeting one.
 *
 * A BLANK IS NOT THE ANSWER EITHER, and that is why the DATE still renders
 * beside this. "Changed on 2026-01-05 14:00" with no name is visibly a
 * missing name; an entirely empty row would read as a screen that forgot to
 * fetch. The cell is narrow and honest rather than wide and wrong.
 *
 * When decision 28 lands, THIS IS THE ONLY FUNCTION THAT CHANGES. That is the
 * whole reason it exists as a named seam rather than as a conditional spread
 * across ten screens.
 */
export function unrecordedAuthorLabel(): string | null {
  return null;
}

/**
 * Three states, and the third is the one that is easy to lose.
 *
 * A name present is a name. A NULL id is the seam above. But an id PRESENT
 * with a NULL name is a THIRD state — the user row was removed — and it is
 * fully distinguishable on the wire, so it is answerable today and gets a real
 * answer instead of falling into the seam. `authorship()` in
 * `apps/api/src/platform/authorship.ts` yields a NULL name rather than a raw
 * uuid in that case, on the grounds that showing a uuid where a person's name
 * belongs is exactly the failure the rule's verb is about.
 *
 * The CRM activity log is the in-house precedent for this pair and it does
 * NOT do this — `leads-page.tsx:638` is `{entry.createdByName ? … : null}`,
 * which silently drops a removed user and a system act alike. That is the
 * blank this rule exists to prevent, so the precedent is followed on format
 * and deliberately not followed on emptiness.
 */
function authorLabel(
  id: string | null,
  name: string | null,
  removedLabel: string,
): string | null {
  if (name !== null) return name;
  if (id !== null) return removedLabel;
  return unrecordedAuthorLabel();
}

function AuthorAndDate({
  id,
  name,
  at,
  removedLabel,
  testId,
}: {
  id: string | null;
  name: string | null;
  at: string;
  removedLabel: string;
  testId: string;
}) {
  const who = authorLabel(id, name, removedLabel);
  return (
    <span data-testid={testId}>
      {who === null ? null : <span>{who} · </span>}
      <Ltr value={at.slice(0, 16).replace('T', ' ')} className="font-mono" />
    </span>
  );
}

/**
 * Both facts, for one master record.
 *
 * `createdAt` and `updatedAt` are non-nullable on all nine shapes, so the two
 * dates always render and the rule's "and when" halves are never in doubt —
 * ON EIGHT OF THE NINE TABLES. `changedAtIsNotAChangeTime` is the ninth.
 *
 * ── WHY MD-13 GETS A FLAG AND NOT A COPY OF THIS COMPONENT ────────────────
 *
 * `number_sequences.updated_at` DOES NOT MEAN "when this record was last
 * changed". `number-sequences.ts` says so on the shape itself, and the
 * migration that added the columns says it again: the
 * `number_sequences_forward_only()` trigger stamps `updated_at` on the
 * counter bump inside `app_next_number()`, so it moves EVERY TIME ANY
 * DOCUMENT IN THE PRODUCT IS NUMBERED. It means "when a number was last
 * issued". `updated_by` is different — the allocator never writes it, only
 * the MD-13 save does — so the NAME is a true answer about who last changed
 * the setting while the TIMESTAMP beside it is about something else
 * entirely.
 *
 * The contract states the consequence in as many words: *"A screen pairing
 * the two as 'changed by X at T' would be wrong on this one table; show the
 * author against the save, not against this timestamp."* So this flag drops
 * the timestamp from the changed line rather than printing a number that
 * would be read as a change time and is not one.
 *
 * THIS LEAVES MD-RULE-5's "and when" GENUINELY UNMET ON MD-13, and that is rule-coverage:ignore
 * reported rather than papered over. The "when" of a settings change is not
 * recorded anywhere on that table today, so the choice is between showing
 * nothing and showing a figure that is wrong. A wrong timestamp on an audit
 * field is worse than an absent one: it is checkable, someone will check it
 * against the audit log, and it will not match. Closing it properly needs a
 * column the table does not have, which is a migration and not a screen.
 */
/*
 * `ChangedOnLine` — the date-only interim for master kinds whose wire
 * carried no author facts — lived here until 20260902100000 closed the last
 * five tables. Its one user (MD-08) now draws the full `<AuthorshipLine>`,
 * so the component is gone rather than kept as dead code. Its label key was
 * retired from both dictionaries in the same change that removed its pin —
 * a key whose only-ever consumer is deleted is dead, not half-landed wiring.
 */

/**
 * `updatedAt` admits NULL for exactly one master kind: `accounts`, whose
 * `updated_at` column arrived late (20260902100000) and without a default —
 * a backfilled date would have claimed every old account changed at
 * migration time. NULL therefore means "no change recorded", and the line
 * draws NOTHING for the changed half rather than a date the table never
 * knew. This is the DATE's twin of the decision-28 author seam: an honest
 * absence, not a blank that forgot to load. The other kinds' schemas keep
 * `updatedAt` non-null, so they cannot reach that branch.
 */
export function AuthorshipLine({
  record,
  changedAtIsNotAChangeTime = false,
}: {
  record: Omit<Authorship, 'updatedAt'> & { updatedAt: string | null };
  changedAtIsNotAChangeTime?: boolean;
}) {
  const { t } = useTranslation();
  const removedLabel = t('md.authorRemoved');
  const changedBy = authorLabel(record.updatedBy, record.updatedByName, removedLabel);
  return (
    <div
      className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground"
      data-testid="authorship"
    >
      <span>
        {t('md.createdByOn')}{' '}
        <AuthorAndDate
          id={record.createdBy}
          name={record.createdByName}
          at={record.createdAt}
          removedLabel={removedLabel}
          testId="authorship-created"
        />
      </span>
      {changedAtIsNotAChangeTime || record.updatedAt === null ? (
        // No name means no true fact to show at all here, because the
        // timestamp that would normally carry the line is disqualified above
        // (MD-13's mismeaning stamp) or absent (accounts' "no change
        // recorded").
        changedBy === null ? null : (
          <span>
            {t('md.changedByOn')} <span data-testid="authorship-changed">{changedBy}</span>
          </span>
        )
      ) : (
        <span>
          {t('md.changedByOn')}{' '}
          <AuthorAndDate
            id={record.updatedBy}
            name={record.updatedByName}
            at={record.updatedAt}
            removedLabel={removedLabel}
            testId="authorship-changed"
          />
        </span>
      )}
    </div>
  );
}
